// URL-param-synced state for the Time view (T-143, time-view-implementation-
// plan.md Step 5). Mirrors the pattern used by `useOrgViewFilters`: the URL
// is the canonical source so deep-links work, back/forward restore state,
// and the page can be reloaded without losing toolbar selections.
//
// Params:
//   - tab        → 'calendar' (default) | 'manage'
//   - weekStart  → ISO YYYY-MM-DD Monday for the Calendar tab
//   - site       → site id (or 'all', the default — omitted from URL)
//   - team       → team id (or 'all', the default — omitted from URL)
//   - type       → TimeOffType (Calendar tab only; or 'all', omitted)
//
// Setters omit the param when the value equals the default (keeps URLs
// short, matches the Org View convention).

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { TimeOffType } from '@pa/shared/time-view';

export type TimeViewTab = 'calendar' | 'manage';

export interface TimeViewUrlState {
  tab: TimeViewTab;
  weekStart: string; // ISO YYYY-MM-DD Monday
  site: string;      // 'all' or site id
  team: string;      // 'all' or team id
  type: TimeOffType | 'all';
  /**
   * T-231 — when true the Week-variant body hides Sat/Sun columns and
   * widens Mon-Fri to 192px. URL form: `?hideWeekends=1`. Omitted when
   * false to keep URLs short.
   */
  hideWeekends: boolean;
}

export const TIME_VIEW_DEFAULTS = {
  tab: 'calendar' as TimeViewTab,
  site: 'all',
  team: 'all',
  type: 'all' as const,
  hideWeekends: false as const,
};

const VALID_TAB = new Set<TimeViewTab>(['calendar', 'manage']);
const VALID_TYPE = new Set<string>([
  'Vacation',
  'Sick',
  'Child Sick',
  'Work from Home',
  'Parental Leave',
]);

/**
 * Return the Monday of the ISO week containing `d` as a YYYY-MM-DD string
 * (UTC). Mondays drive the calendar's 7-day window per the API contract
 * for `GET /api/timeoff/calendar` (T-140).
 */
export function isoMonday(d: Date = new Date()): string {
  const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  // JS getUTCDay returns 0=Sun..6=Sat. We want Monday-based: 0=Mon..6=Sun.
  const dow = (utc.getUTCDay() + 6) % 7;
  utc.setUTCDate(utc.getUTCDate() - dow);
  return utc.toISOString().slice(0, 10);
}

/**
 * Shift an ISO YYYY-MM-DD date by `deltaDays` (UTC), returning the
 * normalised YYYY-MM-DD string. Used to step the calendar one week
 * forward / back.
 */
export function shiftIsoDate(iso: string, deltaDays: number): string {
  const [y, m, d] = iso.split('-').map((s) => Number(s));
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + deltaDays);
  return dt.toISOString().slice(0, 10);
}

export interface UseTimeViewUrlStateResult {
  state: TimeViewUrlState;
  setTab: (next: TimeViewTab) => void;
  setWeekStart: (next: string) => void;
  shiftWeek: (deltaWeeks: number) => void;
  resetToToday: () => void;
  setSite: (next: string) => void;
  setTeam: (next: string) => void;
  setType: (next: TimeOffType | 'all') => void;
  /**
   * T-231 — toggle the Week-variant's "Hide weekends" column trim.
   * `true` is wire-encoded as `?hideWeekends=1`; `false` drops the
   * param.
   */
  setHideWeekends: (next: boolean) => void;
}

export function useTimeViewUrlState(): UseTimeViewUrlStateResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<TimeViewUrlState>(() => {
    const rawTab = searchParams.get('tab');
    const tab: TimeViewTab =
      rawTab && VALID_TAB.has(rawTab as TimeViewTab)
        ? (rawTab as TimeViewTab)
        : TIME_VIEW_DEFAULTS.tab;
    const rawWeek = searchParams.get('weekStart');
    const weekStart =
      rawWeek && /^\d{4}-\d{2}-\d{2}$/.test(rawWeek) ? rawWeek : isoMonday();
    const rawType = searchParams.get('type');
    const type: TimeOffType | 'all' =
      rawType && VALID_TYPE.has(rawType) ? (rawType as TimeOffType) : 'all';
    const rawHideWeekends = searchParams.get('hideWeekends');
    const hideWeekends =
      rawHideWeekends === '1' || rawHideWeekends === 'true';
    return {
      tab,
      weekStart,
      site: searchParams.get('site') ?? TIME_VIEW_DEFAULTS.site,
      team: searchParams.get('team') ?? TIME_VIEW_DEFAULTS.team,
      type,
      hideWeekends,
    };
  }, [searchParams]);

  const updateMany = useCallback(
    (entries: Array<[string, string | null, string | null]>) => {
      // entries: [key, value, defaultValue]; null/default deletes the key.
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value, defaultValue] of entries) {
            if (value == null || value === '' || value === defaultValue) {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          }
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const setTab = useCallback(
    (next: TimeViewTab) =>
      updateMany([['tab', next, TIME_VIEW_DEFAULTS.tab]]),
    [updateMany],
  );
  const setWeekStart = useCallback(
    (next: string) => updateMany([['weekStart', next, null]]),
    [updateMany],
  );
  const shiftWeek = useCallback(
    (deltaWeeks: number) => {
      const current = state.weekStart;
      const shifted = shiftIsoDate(current, deltaWeeks * 7);
      updateMany([['weekStart', shifted, null]]);
    },
    [state.weekStart, updateMany],
  );
  const resetToToday = useCallback(() => {
    updateMany([['weekStart', null, null]]);
  }, [updateMany]);
  const setSite = useCallback(
    (next: string) => updateMany([['site', next, TIME_VIEW_DEFAULTS.site]]),
    [updateMany],
  );
  const setTeam = useCallback(
    (next: string) => updateMany([['team', next, TIME_VIEW_DEFAULTS.team]]),
    [updateMany],
  );
  const setType = useCallback(
    (next: TimeOffType | 'all') =>
      updateMany([['type', next, TIME_VIEW_DEFAULTS.type]]),
    [updateMany],
  );
  const setHideWeekends = useCallback(
    (next: boolean) =>
      // false is the default — drop the URL param to keep URLs short.
      updateMany([['hideWeekends', next ? '1' : null, null]]),
    [updateMany],
  );

  return {
    state,
    setTab,
    setWeekStart,
    shiftWeek,
    resetToToday,
    setSite,
    setTeam,
    setType,
    setHideWeekends,
  };
}
